/* === AgTabs — Anagogia Design System === */

import { Children, cloneElement, isValidElement } from 'react';
import styles from './AgTabs.module.css';

/* ------------------------------------------------------------------ */
/*  AgTab                                                              */
/* ------------------------------------------------------------------ */

interface AgTabProps {
  /** Tab label (text or ReactNode) */
  label: React.ReactNode;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Icon position relative to label */
  iconPosition?: 'start' | 'top';
  disabled?: boolean;
  className?: string;
  /** @internal — set by AgTabs parent */
  _index?: number;
  /** @internal */
  _active?: boolean;
  /** @internal */
  _onClick?: (index: number) => void;
}

/**
 * Onglet individuel d'une barre AgTabs.
 * @category Affichage
 */
function AgTab({
  label,
  icon,
  iconPosition = 'start',
  disabled = false,
  className,
  _index = 0,
  _active = false,
  _onClick,
}: AgTabProps) {
  const handleClick = () => {
    if (!disabled && _onClick) _onClick(_index);
  };

  const cls = [
    styles.tab,
    _active ? styles.tabActive : undefined,
    disabled ? styles.tabDisabled : undefined,
    icon && iconPosition === 'top' ? styles.tabIconTop : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={cls}
      role="tab"
      type="button"
      aria-selected={_active}
      aria-disabled={disabled || undefined}
      tabIndex={_active ? 0 : -1}
      disabled={disabled}
      onClick={handleClick}
    >
      {icon && <span className={styles.tabIcon}>{icon}</span>}
      <span className={styles.tabLabel}>{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  AgTabs                                                             */
/* ------------------------------------------------------------------ */

interface AgTabsProps {
  /** Index of the active tab */
  value: number;
  /** Called when user clicks a tab */
  onChange: (value: number) => void;
  /** Layout variant */
  variant?: 'default' | 'fullWidth';
  children: React.ReactNode;
  className?: string;
}

/**
 * Barre d'onglets contrôlée (contient des AgTab).
 * @category Affichage
 */
function AgTabs({
  value,
  onChange,
  variant = 'default',
  children,
  className,
}: AgTabsProps) {
  const cls = [
    styles.tabs,
    variant === 'fullWidth' ? styles.tabsFullWidth : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls} role="tablist">
      {Children.map(children, (child, index) => {
        if (!isValidElement(child)) return child;
        return cloneElement(child as React.ReactElement<AgTabProps>, {
          _index: index,
          _active: index === value,
          _onClick: onChange,
        });
      })}
    </div>
  );
}

export { AgTabs, AgTab };
export type { AgTabsProps, AgTabProps };
export default AgTabs;
